import { dateUtils } from '@festina-life/core'
import { useSvgTextSize } from '@festina-life/ui/hooks'
import { mergeRefs } from '@festina-life/ui/libs'
import { Group } from '@visx/group'
import { Line } from '@visx/shape'
import dayjs from 'dayjs'
import { Fragment, useMemo } from 'react'

import type { TimelineUnit } from '../templateTimeline/types'
import { filterOverlappingTimeIndicators, getTimeIndicators } from './helpers'
import { useTimelineScale } from './useTimelineScale'

type Props = {
  /** Identifier assigned to the draggable current-time line. */
  currentTimeLineId: string
  /** Initial time */
  from: Date
  /** End time */
  to: Date
  /** Current date */
  currentDate: Date
  /** End Y offset of the timeline indicators */
  endY: number
  /** Current length in pixels of the timeline. */
  lengthInPixels: number
  /** Units in which the time should be rendered. */
  unit: TimelineUnit
}

export const TimeIndicators = ({
  currentTimeLineId,
  from,
  to,
  currentDate,
  unit,
  lengthInPixels,
  endY,
}: Props) => {
  const { dateToPixel } = useTimelineScale({
    timeLineWidthPx: lengthInPixels,
    startDate: dateUtils.dateUtcAsLocal(from).toDate(),
    endDate: dateUtils.dateUtcAsLocal(to).toDate(),
  })
  // Time indicators should be regenerated when the language settings change since they are locale dependant.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const allIndicators = useMemo(
    () => getTimeIndicators({ fromDate: from, toDate: to, unit }),
    [from, to, unit],
  )
  const {
    dimensions: { width: labelWidth, height: textHeight },
    svgRef,
  } = useSvgTextSize<SVGGElement>({
    text: allIndicators.length ? allIndicators[0].label : 'default',
  })
  const currentDateLabel = dayjs(currentDate).format('L')
  const {
    dimensions: { width: currentDateWidth },
    svgRef: currentDateSvgRef,
  } = useSvgTextSize<SVGGElement>({ text: currentDateLabel })
  const filteredIndicators = useMemo(
    () =>
      filterOverlappingTimeIndicators({
        timelineLengthInPx: lengthInPixels,
        labelWidthInPixels: labelWidth,
        timeIndicators: allIndicators,
        timeToPxFn: dateToPixel,
      }),
    [lengthInPixels, labelWidth, allIndicators, dateToPixel],
  )
  const currentDateIndicatorOffset = dateToPixel(currentDate)

  return (
    <Group innerRef={mergeRefs(svgRef, currentDateSvgRef)}>
      <>
        <text
          className="fill-gray-400 text-sm dark:fill-amaze-inkBright"
          dominantBaseline="hanging"
          x={currentDateIndicatorOffset - currentDateWidth / 2}
          y={endY + textHeight / 2}
          fontWeight="bolder"
        >
          {currentDateLabel}
        </text>
        <Line
          id={currentTimeLineId}
          className="cursor-ew-resize stroke-gray-400 stroke-4 dark:stroke-amaze-inkBright"
          from={{ x: currentDateIndicatorOffset, y: 0 }}
          to={{ x: currentDateIndicatorOffset, y: endY }}
        />
      </>
      {filteredIndicators.map(({ label, date }) => {
        const indicatorOffset = dateToPixel(date)

        return (
          <Fragment key={date.toISOString()}>
            <text
              className="fill-amaze-ink text-sm dark:fill-gray-400"
              dominantBaseline="hanging"
              x={indicatorOffset - labelWidth}
            >
              {label}
            </text>
            <Line
              className="stroke-gray-400 stroke-1"
              from={{ x: indicatorOffset, y: 0 }}
              to={{ x: indicatorOffset, y: endY }}
            />
          </Fragment>
        )
      })}
    </Group>
  )
}
